import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Facebook, Loader2, ExternalLink, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { labStatus, getPackage, createJob, listHorseJobs, cancelJob, errText } from "@/lib/fbLabApi";
import { ResetGroupPost } from "./ResetGroupPost";
import { UnconfirmedRecovery } from "./UnconfirmedRecovery";
import { VideoPrep } from "./VideoPrep";

const LABEL = {
  queued: "Waiting for the Chrome extension to pick it up", claimed: "Extension picked it up", preparing: "Preparing the post",
  uploading: "Uploading photos and videos", media_ready: "Text and media are in the composer", awaiting_review: "Waiting for you to click Post on Facebook",
  submitting: "Submitting to Facebook", pending_approval: "Submitted — confirming the post on Facebook", published: "Posted — confirmed on Facebook",
  failed: "Failed — nothing was posted", cancelled: "Cancelled", uncertain: "Unconfirmed — check the group before trying again",
  not_posted: "Not posted", superseded: "Superseded (old post deleted)",
};
const ACTIVE = ["queued", "claimed", "preparing", "uploading", "media_ready", "awaiting_review", "submitting", "pending_approval"];
const CANCELLABLE = ["queued", "claimed", "preparing", "uploading"];
const tone = (s, fb) => (fb ? "text-amber-900 bg-amber-50 border-amber-300" : s === "published" ? "text-emerald-800 bg-emerald-50 border-emerald-200" : ["failed", "uncertain"].includes(s) ? "text-red-800 bg-red-50 border-red-200" : "text-blue-800 bg-blue-50 border-blue-200");

function JobStatus({ job, onCancel }) {
  const last = [...(job.events || [])].reverse().find((e) => e.detail && !e.detail.startsWith("Late report ignored"));
  const fallback = job.manual_fallback && job.status === "awaiting_review";
  const pid = job.result?.post_id || (job.result?.post_url || "").match(/posts\/(\d+)/)?.[1];
  return (
    <div data-testid="group-publish-job" className={`rounded-lg border p-3 text-xs space-y-1 break-words [overflow-wrap:anywhere] ${tone(job.status, fallback)}`}>
      <p className="font-semibold flex items-center gap-1.5">
        {ACTIVE.includes(job.status) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        <span data-testid="group-publish-status">{fallback ? "Media couldn't be verified — nothing was posted" : LABEL[job.status] || job.status}</span>
      </p>
      {fallback && <p data-testid="group-publish-fallback" className="text-amber-900 font-medium">Check the open Facebook composer. If it shows the ad text and all photos/videos, click Post there yourself and the link is saved automatically. Otherwise close the composer to cancel.</p>}
      {last && last.detail !== job.error && <p data-testid="group-publish-detail" className="text-stone-700">{last.detail}</p>}
      {job.error && !["published", "superseded"].includes(job.status) && <p data-testid="group-publish-error" className="text-red-800">{job.error}</p>}
      {job.status === "published" && job.result?.post_url && (
        <a data-testid="group-publish-link" href={job.result.post_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#1877F2] hover:underline">
          Open post{pid ? ` (ID ${pid})` : ""} <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {CANCELLABLE.includes(job.status) && !job.submit_clicked_at && (
        <button type="button" data-testid="group-publish-cancel" onClick={onCancel} className="inline-flex items-center gap-1 text-stone-700 hover:underline"><XCircle className="h-3 w-3" />Cancel before posting</button>
      )}
    </div>
  );
}

function ConfirmDialog({ open, onOpenChange, pkg, lab, onGo, busy, hid, onRefresh }) {
  const [testGroup, setTestGroup] = useState(false);
  const [review, setReview] = useState(false);
  const online = lab?.extension?.state === "connected";
  const live = pkg && !pkg.is_test;
  const group = lab?.primary_group_url || "the primary group";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="group-publish-dialog" className="w-[calc(100%-1.5rem)] max-w-md rounded-xl">
        <DialogHeader>
          <DialogTitle>Post to Facebook Group?</DialogTitle>
          <DialogDescription>The Chrome extension {live ? "prepares" : "posts"} the saved Facebook Group ad with its {pkg?.photos ?? 0} photos{pkg?.papers ? `, ${pkg.papers} registration paper image${pkg.papers > 1 ? "s" : ""}` : ""} and {pkg?.videos ?? 0} videos, in ad order, {live ? "in" : "to"} {lab?.primary_group_url || "the primary group"}.</DialogDescription>
        </DialogHeader>
        {!online && <p data-testid="group-publish-offline" className="text-xs text-amber-800">The Chrome extension is {lab?.extension?.state === "never_connected" ? "not connected" : "offline"}. The job waits until Chrome is open with the extension running.</p>}
        {pkg && !pkg.ready && <ul data-testid="group-publish-problems" className="text-xs text-red-800 list-disc pl-4">{pkg.problems.filter((p) => !/Cloudflare is preparing|still processing on Cloudflare/.test(p)).map((p) => <li key={p}>{p}</li>)}</ul>}
        <VideoPrep hid={hid} pkg={pkg} onRefresh={onRefresh} />
        {pkg && !pkg.ready && (pkg.media || []).some((m) => m.kind === "video" && !m.ready) && <p data-testid="group-publish-waiting" className="text-xs text-stone-600">Checking again every 10 seconds. Post to Facebook Group turns on when every video is ready.</p>}
        {live ? (
          <>
            <div data-testid="group-publish-target" className="rounded-lg border border-stone-200 bg-stone-50 p-2 text-xs break-words [overflow-wrap:anywhere]">
              <p className="font-semibold text-stone-900" data-testid="group-publish-target-name">{lab?.primary_group_name || "Primary Facebook Group"}</p>
              <a href={lab?.primary_group_url} target="_blank" rel="noreferrer" data-testid="group-publish-target-url" className="text-[#1877F2] hover:underline">{group}</a>
            </div>
            <p data-testid="group-publish-manual-note" className="text-xs text-stone-700">Facebook opens with the ad text, photos and videos ready. Nothing is posted until you click Post in Facebook yourself; the link is then saved and bid monitoring starts.</p>
            <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="group-publish-confirm-group" checked={testGroup} onCheckedChange={(v) => setTestGroup(!!v)} />This is the correct Facebook Group for this horse.</label>
          </>
        ) : (
          <>
            <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="group-publish-confirm-test" checked={testGroup} onCheckedChange={(v) => setTestGroup(!!v)} />This group is our private TEST group.</label>
            <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="group-publish-review-mode" checked={review} onCheckedChange={(v) => setReview(!!v)} />Let me check the post and click Post myself.</label>
          </>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button data-testid="group-publish-go" disabled={busy || !testGroup || !pkg?.ready} onClick={() => onGo(live || review ? "review" : "publish", live)} className="h-11 rounded-full w-full bg-[#1877F2] hover:bg-[#166FE5]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post to Facebook Group"}
          </Button>
          <Button data-testid="group-publish-dialog-cancel" variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-full w-full">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Shown wherever Facebook Lab is enabled: Horse Workspace → Publishing → Post to Facebook Group through the Chrome extension.
export function GroupAutoPublish({ hid, posted, onChanged }) {
  const [lab, setLab] = useState(undefined);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => listHorseJobs(hid).then((r) => setJobs(r.filter((j) => j.kind === "publish"))).catch(() => {}), [hid]);
  useEffect(() => { labStatus().then(setLab).catch(() => setLab(null)); }, []);
  useEffect(() => { if (lab) { load(); } }, [lab, load]);
  const job = jobs[0];
  const active = job && ACTIVE.includes(job.status);
  useEffect(() => {
    if (!active) { return undefined; }
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active, load]);
  useEffect(() => { if (job?.status === "published") { onChanged?.(); } }, [job?.status, job?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = useCallback(() => getPackage(hid).then(setPkg).catch(() => {}), [hid]);
  const waiting = open && pkg && !pkg.ready && (pkg.media || []).some((m) => m.kind === "video" && !m.ready);
  useEffect(() => {
    if (!waiting) { return undefined; }
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [waiting, refresh]);
  if (!lab) return null;
  const start = async () => {
    setOpen(true); setPkg(null);
    try { setPkg(await getPackage(hid)); } catch (e) { setPkg({ ready: false, problems: [errText(e)] }); }
  };
  const go = async (mode, live) => {
    if (busy) return;
    setBusy(true);
    try { await createJob({ horse_id: hid, mode, ...(live ? { confirm_group: true } : { confirm_test_group: true }) }); toast.success("Publishing job queued — not posted yet"); setOpen(false); load(); }
    catch (e) { toast.error(errText(e)); load(); }
    finally { setBusy(false); }
  };
  const cancel = async () => { try { await cancelJob(job.id); load(); } catch (e) { toast.error(errText(e)); } };
  const blocked = posted || active || job?.status === "uncertain";
  return (
    <section data-testid="group-auto-publish" className="rounded-xl border border-stone-200 bg-white p-4 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Facebook Group · Chrome extension</p>
      <Button data-testid="group-publish-open" disabled={blocked || busy} onClick={start} className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white">
        <Facebook className="h-4 w-4 mr-1.5" />Post to Facebook Group
      </Button>
      {posted && !active && <p data-testid="group-publish-already" className="text-xs text-stone-600">Already posted. Reset the group post before posting again.</p>}
      {job && <JobStatus job={job} onCancel={cancel} />}
      {job?.status === "uncertain" && <UnconfirmedRecovery key={job.id} job={job} onDone={() => { load(); onChanged?.(); }} />}
      <ResetGroupPost key={posted ? "p" : "n"} hid={hid} onChanged={() => { load(); onChanged?.(); }} />
      <ConfirmDialog key={open ? "o" : "c"} open={open} onOpenChange={setOpen} pkg={pkg} lab={lab} onGo={go} busy={busy} hid={hid} onRefresh={refresh} />
    </section>
  );
}
